import React, { useEffect, useRef } from 'react';
import { AlertTriangle, Clock, FileText, Play, User } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { ElementsRenderer } from '../shared';

const AssignmentWelcomeScreen = ({
  assignmentName,
  userName,
  userEmail,
  totalQuestions,
  durationMinutes,
  enableStrictMode,
  instructions,
  startButtonText,
  isLoading,
  isDisabled,
  onStart,
  onLogout
}) => {
  const scrollableRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    scrollableRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleStart = () => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    scrollableRef.current?.scrollTo({ top: 0, behavior: 'instant' });
    onStart();
  };

  return (
    <div className="flex flex-col h-full">
      <div ref={scrollableRef} className="flex-1 overflow-y-auto p-4 pb-32">
        <div className="text-center mb-8 pt-2 md:px-16 lg:px-24 xl:px-32 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <p className="text-sm font-medium text-primary mb-2">Ready to begin?</p>
          <h1 className="text-2xl font-bold text-foreground mb-2 md:text-3xl">{assignmentName}</h1>
          <div className="w-16 md:w-24 h-1 bg-primary mx-auto rounded-full" />
        </div>

        <div className="md:grid md:grid-cols-2 md:gap-6 space-y-6 md:space-y-0 mb-6">
          <Card className="p-6 border border-border/50 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-foreground">Candidate Information</h2>
              <button
                type="button"
                onClick={onLogout}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer underline-offset-4 hover:underline"
              >
                Not you? Logout
              </button>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-primary-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-foreground truncate">{userName}</p>
                <p className="text-sm text-muted-foreground truncate">{userEmail}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6 border border-border/50 shadow-sm">
            <h2 className="text-lg font-medium mb-4 text-foreground">Test Details</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center">
                  <FileText className="w-5 h-5 text-accent-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Questions</p>
                  <p className="font-medium text-foreground">{totalQuestions}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center">
                  <Clock className="w-5 h-5 text-accent-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Duration</p>
                  <p className="font-medium text-foreground">
                    {durationMinutes != null ? `${durationMinutes} mins` : 'No time limit'}
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {enableStrictMode && (
          <Card className="p-6 mb-8 border border-yellow-200 bg-yellow-50 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-medium mb-2 text-yellow-800">Strict Mode Enabled</h2>
                <p className="text-sm text-yellow-700 leading-relaxed">
                  This assignment is in strict mode. Leaving the session or opening a new browser
                  window will submit your progress automatically.
                </p>
              </div>
            </div>
          </Card>
        )}

        <Card className="p-6 mb-8 border border-border/50 shadow-sm">
          <div className="welcome-instructions-content">
            <div className="welcome-callout-green">
              <p>
                <strong>
                ✓ Once you&apos;re ready and have read all instructions, click the &quot;Start
                Test&quot; button below to begin.
                </strong>
              </p>
            </div>
            <ElementsRenderer elements={instructions} variant="welcome" />
          </div>
        </Card>
      </div>

      <Button
        type="button"
        onClick={handleStart}
        disabled={isDisabled || isLoading}
        className="fixed bottom-6 left-4 right-4 md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-2xl z-10 h-12 text-base font-semibold shadow-lg hover:shadow-xl active:scale-[0.99] transition-all"
      >
        {isLoading ? (
          <>Getting the test ready</>
        ) : (
          <>
            <Play className="w-5 h-5" />
            {startButtonText}
          </>
        )}
      </Button>
    </div>
  );
};

export default AssignmentWelcomeScreen;
